import { AsyncPipe, NgClass, NgOptimizedImage } from '@angular/common';
import { Component, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { FormGroup } from '@angular/forms';
import femaleCondomQuestions from '@assets/questionnaires/female-condom-questions.json';
import { CardComponent } from '@components/card/card.component';
import { DynamicFormQuestionComponent } from '@components/dynamic-forms/dynamic-form-question/dynamic-form-question.component';
import { ErrorNotificationComponent } from '@components/error-notification/error-notification.component';
import { OrderFlowUpsellComponent } from '@components/order-flow-upsell/order-flow-upsell.component';
import { FemaleCondomUpsell } from '@data/tests';
import { ConsultationStatus } from '@enums/consultation-status';
import { ConsultationTreatmentTypes } from '@enums/consultation-treatment-types';
import { InputTypes } from '@enums/input-types';
import { Pharmacy } from '@enums/pharmacies';
import { QuestionnaireAnswers } from '@enums/questionnaire-answers';
import { environment } from '@environments/environment';
import { BasicConsultationRequestRequest } from '@models/consultation-request/basic-consultation-request-request';
import { BasicConsultationRequestResponse } from '@models/consultation-request/basic-consultation-request-response';
import { CustomerNote } from '@models/customer-note';
import { Question } from '@models/dynamic-forms/question';
import { FemaleCondomResponse } from '@models/female-condom-response';
import { OrderUpsell } from '@models/order-upsell';
import { UpsellFaq } from '@models/upsell-faq';
import { ImageFallback } from '@pipes/image-fallback';
import { ConsultationRequestService } from '@services/consultation-request.service';
import { DynamicFormsService } from '@services/dynamic-forms.service';
import { ErrorLoggingService } from '@services/error-logging.service';
import { FemaleCondomService } from '@services/female-condom.service';
import { provideDefaultPaymentConfig } from '@services/payment.service';
import { concatMap, Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Component({
  selector: 'app-female-condom',
  imports: [
    AsyncPipe,
    NgOptimizedImage,
    CardComponent,
    ImageFallback,
    ErrorNotificationComponent,
    DynamicFormQuestionComponent,
    NgClass,
  ],
  templateUrl: './female-condom.component.html',
  styleUrls: ['./female-condom.component.scss'],
  providers: [provideDefaultPaymentConfig()],
  encapsulation: ViewEncapsulation.None,
})
export class FemaleCondomComponent extends OrderFlowUpsellComponent implements OnInit {
  public femaleCondomContent: FemaleCondomResponse;
  public showQuestionnaire: boolean = false;
  public isExpandedContent: boolean = false;
  public questions: Question[] = [];
  public form: FormGroup;

  protected readonly femaleCondomTest: OrderUpsell = FemaleCondomUpsell;

  private readonly nitrileAllergyQuestionId = '1';
  private readonly hasAllergiesQuestionId = '2';
  private readonly questionsForProviderQuestionId = '3';
  private readonly insuranceQuestionId = '4';
  private readonly allergiesDetailsQuestionId = '8';

  private femaleCondomService = inject(FemaleCondomService);
  private errorLoggingService = inject(ErrorLoggingService);
  private dynamicFormsService = inject(DynamicFormsService);
  private consultationRequestService = inject(ConsultationRequestService);

  /**
   * Gets the Female Condom upsell test details.
   */
  public override get upsellTest(): OrderUpsell {
    return this.femaleCondomTest;
  }

  /**
   * Initializes the component by fetching the female condom content.
   */
  public ngOnInit(): void {
    this.getFemaleCondomContent();
    this.questions = (<any[]>femaleCondomQuestions).map((question) => new Question(question));
    this.form = this.dynamicFormsService.toFormGroup(this.questions);
  }

  /**
   * Toggles the expanded state of an FAQ item.
   *
   * @param {UpsellFaq} faq the FAQ to toggle
   */
  public toggleFaqItem(faq: UpsellFaq): void {
    faq.isExpanded = !faq.isExpanded;
  }

  /**
   * Toggles the expanded state of the additional content section.
   */
  public toggleContent(): void {
    this.isExpandedContent = !this.isExpandedContent;
  }

  /**
   * Handles the continue action from the first step of the upsell flow, transitioning to the questionnaire form.
   */
  public onContinue(): void {
    this.showQuestionnaire = true;
  }

  /**
   * Goes back to the first step of the upsell flow, allowing the user to review the initial content again.
   */
  public onBack(): void {
    this.showQuestionnaire = false;
  }

  /**
   * Handles the submission of the female condom questionnaire form, validating the form and storing the upsell if valid.
   */
  public onSubmit(): void {
    this.submissionErrors = [];

    if (this.hasDisqualifyingAnswers()) {
      this.navigateToNextPage();

      return;
    }

    if (this.form?.invalid) {
      this.form.markAllAsTouched();

      return;
    }

    this.processing = true;

    this.resolveOrderRequest()
      .pipe(
        concatMap(() =>
          this.orderService.storeUpsell(
            this.upsellTest,
            this.storageService.transactionId,
            this.storageService.order.hash,
          ),
        ),
        concatMap(() =>
          this.orderService
            .storeCustomerNote(
              this.storageService.transactionId,
              this.storageService.order.hash,
              this.getCustomerNote(),
            )
            .pipe(
              catchError((error) => {
                this.errorLoggingService.logError(error);

                return of(null);
              }),
            ),
        ),
      )
      .subscribe({
        next: () => this.handleSuccessfulRequest(),
        error: (error) => this.handleRequestError(error),
      });
  }

  /**
   * Returns true if a trigger question paired with the given question currently has a disqualifying answer.
   *
   * Nitrile allergy and Insurance questions are paired together as triggers, so if the given question is either of those, this method checks
   * the other one for disqualifying answers. For all other questions, this method checks both Nitrile and Insurance questions
   * for disqualifying answers.
   */
  public hasTriggerDisqualifyingAnswer(question: Question): boolean {
    const nitrileAllergyQuestion = this.questions.find((q) => q.id === this.nitrileAllergyQuestionId);
    const insuranceQuestion = this.questions.find((q) => q.id === this.insuranceQuestionId);

    if (question.id === this.nitrileAllergyQuestionId) {
      return this.questionHasDisqualifyingAnswer(insuranceQuestion);
    }

    if (question.id === this.insuranceQuestionId) {
      return this.questionHasDisqualifyingAnswer(nitrileAllergyQuestion);
    }

    return (
      this.questionHasDisqualifyingAnswer(nitrileAllergyQuestion) ||
      this.questionHasDisqualifyingAnswer(insuranceQuestion)
    );
  }

  /** {@inheritDoc OrderFlowUpsellComponent.onUpsellSuccess} */
  protected override onUpsellSuccess(): void {
    this.navigateToNextPage();
  }

  /**
   * Handles a successful response from either the consultation request creation or upsell storage.
   */
  private handleSuccessfulRequest(): void {
    this.processing = false;

    if (!this.isConsultationRequestFlow()) {
      this.orderService.addUpsellTestToStorage(this.upsellTest);
      this.orderService.addUpsellPriceToTotal(this.upsellTest.upsell_price);
    }

    this.onUpsellSuccess();
  }

  /**
   * Fetches the female condom upsell content from the API.
   */
  private getFemaleCondomContent(): void {
    this.loadingService.toggleLoader(true);
    this.femaleCondomService.getFemaleCondomContent().subscribe({
      next: (response) => {
        this.femaleCondomContent = response;
        this.femaleCondomContent.faq_content.faqs.forEach((faq) => (faq.isExpanded = false));
        this.loadingService.toggleLoader(false);
      },
      error: () => {
        this.loadingService.toggleLoader(false);
        setTimeout(() => this.navigateToNextPage(), 0);
      },
    });
  }

  /**
   * Gets the customer note to be stored.
   *
   * The note is constructed by concatenating the labels and values of the questionnaire form, including allergy details
   * if applicable (when the user indicates they have allergies and provides details - e.g ("Do you have any allergies?
   * Yes - pollen, dust"). Each question and answer pair is separated by a line break for readability.
   */
  private getCustomerNote(): CustomerNote {
    const formValue = this.form.value;
    const allergyDetails = formValue[this.allergiesDetailsQuestionId];
    const comment = this.questions
      .filter((question) => question.type !== InputTypes.Checkbox && question.id !== this.allergiesDetailsQuestionId)
      .map((question) => {
        let value = formValue[question.id];

        if (question.id === this.hasAllergiesQuestionId && allergyDetails) {
          value = `${value} - ${allergyDetails}`;
        }

        return this.getAnswerWithLabel(question.id, value);
      })
      .filter(Boolean)
      .join('<br/>');

    return {
      customer_notes_comment: comment,
      customer_notes_attention_mark: 'NO',
    };
  }

  /**
   * Gets the form answer for a question, prefixed with its label if one exists.
   *
   * @param {string} questionId the ID of the question to look up
   * @param {string} value optional override value, falls back to the current form value for the question
   *
   * @returns {string} "label: answer" if the question has a label, raw answer if not, or "" if there is no answer
   */
  private getAnswerWithLabel(questionId: string, value?: string): string {
    const question = this.questions.find((question) => question.id === questionId);
    const answer: string = value ?? this.form.value[questionId] ?? '';

    if (!answer) {
      return '';
    }

    return question?.label ? `${question.label}: ${answer}` : answer;
  }

  /**
   * Checks if the form has any answers that would disqualify the user from purchasing the female condom.
   */
  private hasDisqualifyingAnswers(): boolean {
    return this.questions.some((question) => this.questionHasDisqualifyingAnswer(question));
  }

  /**
   * Returns true if the given question currently has a disqualifying answer based on its visible warnings.
   */
  private questionHasDisqualifyingAnswer(question: Question): boolean {
    return this.dynamicFormsService.getQuestionVisibleWarnings(question, this.form).length > 0;
  }

  /**
   * Returns true if the current upsell flow is configured to create a consultation request instead of directly storing the upsell.
   */
  private isConsultationRequestFlow(): boolean {
    return environment.order.triggerFemaleCondomUpsellConsultation;
  }

  /**
   * Resolves order request, either by creating a consultation request or doing nothing, depending on the flow configuration.
   */
  private resolveOrderRequest(): Observable<BasicConsultationRequestResponse | null> {
    if (this.isConsultationRequestFlow()) {
      return this.createConsultationRequest();
    }

    return of(null);
  }

  /**
   * Create basic female-condom consultation request.
   */
  private createConsultationRequest(): Observable<BasicConsultationRequestResponse | null> {
    const order = this.storageService.order;

    const allergies =
      this.form.value[this.hasAllergiesQuestionId] === QuestionnaireAnswers.Yes
        ? (this.form.value[this.allergiesDetailsQuestionId] ?? '')
        : '';
    const healthInfo = this.getAnswerWithLabel(this.questionsForProviderQuestionId);
    const consultationRequest = new BasicConsultationRequestRequest(
      order.transaction_id,
      ConsultationTreatmentTypes.FemaleCondom,
      order.first_name,
      order.last_name,
      order.date_of_birth,
      order.email,
      ConsultationStatus.Pending,
      Pharmacy.ZEAL_SPECIALTY,
      allergies,
      healthInfo,
    );

    return this.consultationRequestService
      .postBasicConsultationRequest(consultationRequest, order.hash, order.transaction_id)
      .pipe(
        catchError((error) => {
          this.errorLoggingService.logError(error);

          return of(null);
        }),
      );
  }
}
